import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema } from './common';

/** Event catalog (SDD §15.2): the v1.2 catalog carried forward in full plus payout.recorded, account.reset,
 *  price_alert.triggered, backtest.completed (R-05, R-09, R-03). Past-tense facts, never commands. */
export const EVENT_TYPES = [
  'trade.opened',
  'trade.closed',
  'rule.breached',
  'lockout.created',
  'lockout.cleared',
  'session.armed',
  'session.overridden',
  'session.closed',
  'checklist.logged',
  'journal.created',
  'journal.tagged',
  'score.updated',
  'score.drop_detected',
  'signal.published',
  'signal.outcome',
  'copy.executed',
  'copy.rejected',
  'report.issued',
  'digest.issued',
  'brief.generated',
  'suggestion.created',
  'account.linked',
  'account.sync_degraded',
  'drift.detected',
  'news.updated',
  'calendar.updated',
  'grant.created',
  'grant.revoked',
  'plan.changed',
  'profile.versioned',
  'ruleset.versioned',
  'strategy.versioned',
  'payout.recorded',
  'account.reset',
  'price_alert.triggered',
  'backtest.completed',
] as const;
export const EventTypeSchema = z.enum(EVENT_TYPES);
export type EventType = z.infer<typeof EventTypeSchema>;

/** Aggregate roots an event may belong to (the `aggregate` field of the envelope). */
export const EVENT_AGGREGATES = [
  'trade',
  'rule',
  'lockout',
  'session',
  'checklist',
  'journal',
  'score',
  'signal',
  'copy',
  'report',
  'digest',
  'brief',
  'suggestion',
  'account',
  'drift',
  'news',
  'calendar',
  'grant',
  'plan',
  'profile',
  'ruleset',
  'strategy',
  'payout',
  'price_alert',
  'backtest',
  'order',
] as const;
export const EventAggregateSchema = z.enum(EVENT_AGGREGATES);
export type EventAggregate = z.infer<typeof EventAggregateSchema>;

/** Envelope (SDD §15.1). `id` is a ULID (ordered per aggregate); `payload` is zod-validated per type via `eventOf`. */
export const EventEnvelopeSchema = z
  .object({
    id: IdSchema,
    type: EventTypeSchema,
    version: z.number().int().positive(),
    occurredAt: IsoDateTimeSchema,
    userId: IdSchema,
    accountId: IdSchema.nullable(),
    sessionId: IdSchema.nullable(),
    aggregate: EventAggregateSchema,
    aggregateId: IdSchema,
    payload: z.record(z.string(), z.unknown()),
  })
  .strict();
export type EventEnvelope = z.infer<typeof EventEnvelopeSchema>;

/** Narrow the envelope to one event type with a typed payload. Producers declare these next to their procedures. */
export function eventOf<T extends EventType, P extends z.ZodType>(type: T, payload: P) {
  return EventEnvelopeSchema.extend({ type: z.literal(type), payload });
}

/** Payloads the Phase-1 sim produces (the outbox rows it records). More are declared as producers land. */
export const TradeOpenedPayloadSchema = z
  .object({
    orderId: IdSchema,
    positionId: IdSchema,
    symbol: z.string(),
    side: z.enum(['BUY', 'SELL']),
    volume: z.number(),
    price: z.number(),
    intentId: IdSchema,
  })
  .strict();
export const TradeClosedPayloadSchema = z
  .object({
    positionId: IdSchema,
    symbol: z.string(),
    closePrice: z.number(),
    pnl: z.number(),
    partial: z.boolean(),
  })
  .strict();
export const SessionArmedPayloadSchema = z
  .object({
    sessionId: IdSchema,
    strategies: z.array(z.string()),
    maxTrades: z.number().int(),
    bypassed: z.boolean(),
  })
  .strict();
export const SessionClosedPayloadSchema = z
  .object({ sessionId: IdSchema, reflection: z.string().nullable() })
  .strict();
export const JournalCreatedPayloadSchema = z
  .object({ entryId: IdSchema, mode: z.enum(['text', 'voice']), sessionId: IdSchema.nullable() })
  .strict();
export const ChecklistLoggedPayloadSchema = z
  .object({
    checklistId: IdSchema,
    phase: z.enum(['pre', 'in', 'post']),
    completed: z.number().int(),
    total: z.number().int(),
  })
  .strict();

export const TradeOpenedEventSchema = eventOf('trade.opened', TradeOpenedPayloadSchema);
export const TradeClosedEventSchema = eventOf('trade.closed', TradeClosedPayloadSchema);
export const SessionArmedEventSchema = eventOf('session.armed', SessionArmedPayloadSchema);
export const SessionClosedEventSchema = eventOf('session.closed', SessionClosedPayloadSchema);
export const JournalCreatedEventSchema = eventOf('journal.created', JournalCreatedPayloadSchema);
export const ChecklistLoggedEventSchema = eventOf('checklist.logged', ChecklistLoggedPayloadSchema);
